import React, { useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  Home, Building, Building2, Store, Hotel, Warehouse, LayoutGrid,
  ArrowRight, ArrowLeft, CheckCircle2, Zap, Images, Ruler, Layers,
  Compass, Sparkles, Boxes, DoorOpen,
} from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { useWorkingModel } from "@/hooks/useWorkingModel";
import { Button, Card, Input, Badge, Spinner } from "@/components/ui";

/**
 * DesignWizard - a fully INDEPENDENT, premium 5-step guided flow that reuses
 * the existing generation engine (villa / apartment / generic + variants).
 * It does NOT modify Studio or the legacy /wizard; it is purely additive.
 *
 * Steps: 1) What to build · 2) Structure & floors · 3) Land & neighbours ·
 *        4) Spaces & amenities · 5) Review & generate.
 */

const KINDS = [
  { id: "villa", icon: Home, ar: "فيلا سكنية", en: "Villa", dar: "سكن عائلي فاخر بغرف وصالات ومجالس", den: "Luxury family home" },
  { id: "apartment", icon: Building, ar: "شقة سكنية", en: "Apartment", dar: "وحدة سكنية بطابق واحد أو أكثر", den: "Residential unit" },
  { id: "apartments_floor", icon: Building2, ar: "عمارة شقق", en: "Apartments floor", dar: "طابق يضم عدة شقق مع صالة توزيع", den: "Multi-unit floor" },
  { id: "office", icon: Briefcase_, ar: "مبنى مكاتب", en: "Office", dar: "مكاتب + استقبال + قاعة اجتماعات", den: "Offices + meeting rooms" },
  { id: "hotel", icon: Hotel, ar: "فندق", en: "Hotel", dar: "غرف نُزلاء + لوبي + خدمات", den: "Guest rooms + lobby" },
  { id: "shops", icon: Store, ar: "محلات تجارية", en: "Shops", dar: "محلات بواجهات + مخازن خلفية", den: "Retail shops + storage" },
  { id: "mall", icon: LayoutGrid, ar: "مجمّع تجاري", en: "Mall", dar: "محلات + ممرات + ساحة وسطى", den: "Retail + atrium" },
  { id: "studio_apartments", icon: Warehouse, ar: "استوديوهات", en: "Studios", dar: "وحدات استوديو مدمجة", den: "Compact studio units" },
];

function Briefcase_(props) { return <Boxes {...props} />; }

const SYSTEMS = [
  { id: "rc_frame", ar: "هيكل خرساني (أعمدة وجسور)", en: "RC frame" },
  { id: "load_bearing", ar: "جدران حاملة", en: "Load-bearing walls" },
  { id: "steel", ar: "هيكل حديدي", en: "Steel frame" },
];
const CODES = [
  { id: "SBC", dc: "SBC_304", ar: "السعودي SBC 304", en: "Saudi SBC 304" },
  { id: "ACI", dc: "ACI_318", ar: "الأمريكي ACI 318", en: "ACI 318" },
  { id: "ECP", dc: "ECP_203", ar: "المصري ECP 203", en: "Egyptian ECP 203" },
  { id: "EC2", dc: "EUROCODE_2", ar: "الأوروبي Eurocode 2", en: "Eurocode 2" },
  { id: "BS", dc: "BS_8110", ar: "البريطاني BS 8110", en: "BS 8110" },
  { id: "IS", dc: "IS_456", ar: "الهندي IS 456", en: "IS 456" },
  { id: "SY", dc: "SY_ARAB", ar: "السوري (العربي السوري)", en: "Syrian Arab Code" },
];
const ROOFS = [
  { id: "solid", ar: "بلاطة مصمتة", en: "Solid slab" },
  { id: "hordi_regular", ar: "هوردي عادي (صب موقعي)", en: "Conventional Hourdi" },
  { id: "hordi_precast", ar: "هوردي بأعصاب جاهزة", en: "Precast-joist Hourdi" },
  { id: "hordi_precast_block", ar: "هوردي + بلوك مفرغ", en: "Hourdi + hollow block" },
  { id: "flat", ar: "بلاطة مسطحة (بلا كمرات)", en: "Flat slab" },
  { id: "waffle", ar: "بلاطة وافل", en: "Waffle slab" },
  { id: "post_tensioned", ar: "سابقة الإجهاد PT", en: "Post-tensioned" },
  { id: "composite_steel", ar: "فولاذية مركبة", en: "Composite steel deck" },
];
const BASEMENT_USES = [
  { id: "parking", ar: "مواقف سيارات", en: "Parking" },
  { id: "storage", ar: "مستودعات", en: "Storage" },
  { id: "living", ar: "فراغات معيشة", en: "Living spaces" },
];
const NEIGHBOR_SIDES = [
  { id: "left", ar: "يسار", en: "Left" },
  { id: "right", ar: "يمين", en: "Right" },
  { id: "rear", ar: "خلف", en: "Rear" },
];
const AMENITIES = [
  { id: "has_majlis", ar: "مجلس", en: "Majlis" },
  { id: "has_office", ar: "مكتب", en: "Office" },
  { id: "has_maid_room", ar: "غرفة خادمة", en: "Maid's room" },
  { id: "has_garage", ar: "كراج", en: "Garage" },
  { id: "has_pool", ar: "مسبح", en: "Pool" },
  { id: "has_garden", ar: "حديقة", en: "Garden" },
  { id: "has_courtyard", ar: "فناء داخلي", en: "Courtyard" },
  { id: "has_balconies", ar: "شرفات", en: "Balconies" },
];

const STEP_META = [
  { icon: Home, ar: "ماذا تبني؟", en: "What to build" },
  { icon: Layers, ar: "الهيكل والطوابق", en: "Structure & floors" },
  { icon: Compass, ar: "الأرض والجيران", en: "Land & neighbours" },
  { icon: DoorOpen, ar: "الفراغات", en: "Spaces" },
  { icon: CheckCircle2, ar: "المراجعة", en: "Review" },
];

export default function DesignWizard() {
  const { lang } = useAuth();
  const isAr = lang === "ar";
  const navigate = useNavigate();
  const [, setWm] = useWorkingModel();

  const [step, setStep] = useState(0);
  const [busy, setBusy] = useState(null);

  // Selections
  const [kind, setKind] = useState("villa");
  const [system, setSystem] = useState("rc_frame");
  const [code, setCode] = useState("SBC");
  const [slab, setSlab] = useState("solid");
  const [floors, setFloors] = useState(1);
  const [hasBasement, setHasBasement] = useState(false);
  const [basementUse, setBasementUse] = useState("parking");
  const [plotW, setPlotW] = useState("20");
  const [plotH, setPlotH] = useState("25");
  const [setFront, setSetFront] = useState("3");
  const [setSide, setSetSide] = useState("2");
  const [setRear, setSetRear] = useState("3");
  const [neighbors, setNeighbors] = useState([]);
  const [numApartments, setNumApartments] = useState("2");
  const [bedroomsPerUnit, setBedroomsPerUnit] = useState("2");
  const [regularBeds, setRegularBeds] = useState("2");
  const [kidsRooms, setKidsRooms] = useState("1");
  const [masterEnsuite, setMasterEnsuite] = useState(true);
  const [baths, setBaths] = useState("2");
  const [kitchens, setKitchens] = useState("1");
  const [numFrontShops, setNumFrontShops] = useState("0");
  const [amen, setAmen] = useState(["has_majlis", "has_balconies"]);

  const isResidential = kind === "villa" || kind === "apartment";
  const isMultiUnit = kind === "apartments_floor";
  const neighborStr = neighbors.length ? neighbors.join(",") : "none";
  const area = (parseFloat(plotW) || 0) * (parseFloat(plotH) || 0);
  const totalBeds = (masterEnsuite ? 1 : 0) + (parseInt(regularBeds, 10) || 0) + (parseInt(kidsRooms, 10) || 0);

  const toggleAmen = (id) => setAmen((p) => (p.includes(id) ? p.filter((x) => x !== id) : [...p, id]));
  const toggleNeighbor = (id) => setNeighbors((p) => (p.includes(id) ? p.filter((x) => x !== id) : [...p, id]));
  const has = (id) => amen.includes(id);

  const selectedKind = KINDS.find((k) => k.id === kind);

  const briefParams = useMemo(() => {
    const b = {
      kind, plot_w: parseFloat(plotW) || 10, plot_h: parseFloat(plotH) || 10,
      floors: parseInt(floors, 10) || 1, lang,
      structural_system: system, code_profile: code,
      neighbor_side: neighborStr,
    };
    if (isMultiUnit) {
      b.num_apartments = Math.max(1, parseInt(numApartments, 10) || 1);
      b.bedrooms_per_unit = parseInt(bedroomsPerUnit, 10) || 0;
    }
    if (isResidential) {
      b.bedrooms = Math.max(1, totalBeds);
      b.regular_bedrooms = parseInt(regularBeds, 10) || 0;
      b.kids_rooms = parseInt(kidsRooms, 10) || 0;
      b.bathrooms = parseInt(baths, 10) || 1;
      b.num_kitchens = parseInt(kitchens, 10) || 1;
      b.has_master_ensuite = masterEnsuite;
      AMENITIES.forEach((a) => { if (has(a.id)) b[a.id] = true; });
      if (hasBasement) { b.has_basement = true; b.basement_use = basementUse; }
      if ((parseInt(numFrontShops, 10) || 0) > 0) { b.has_front_shops = true; b.num_front_shops = parseInt(numFrontShops, 10); }
    }
    return b;
  }, [kind, plotW, plotH, floors, lang, system, code, neighborStr, isResidential, isMultiUnit, numApartments, bedroomsPerUnit, totalBeds, regularBeds, kidsRooms, baths, kitchens, masterEnsuite, amen, hasBasement, basementUse, numFrontShops]);

  function goVariants() {
    const q = new URLSearchParams();
    Object.entries(briefParams).forEach(([k, v]) => q.set(k, String(v)));
    q.set("setback_front", setFront); q.set("setback_side", setSide); q.set("setback_rear", setRear);
    navigate(`/variants?${q.toString()}`);
  }

  async function generateOne() {
    setBusy("one");
    try {
      let res;
      if (kind === "villa") {
        res = await api.generateVilla({
          land_w: briefParams.plot_w, land_h: briefParams.plot_h,
          bedrooms: briefParams.bedrooms, bathrooms: briefParams.bathrooms,
          regular_bedrooms: briefParams.regular_bedrooms, kids_rooms: briefParams.kids_rooms,
          num_kitchens: briefParams.num_kitchens, basement_use: hasBasement ? basementUse : undefined,
          has_pool: has("has_pool"), has_garden: has("has_garden"), has_courtyard: has("has_courtyard"),
          has_garage: has("has_garage"), has_maid_room: has("has_maid_room"), has_office: has("has_office"),
          has_master_ensuite: masterEnsuite, has_balconies: has("has_balconies"),
          has_basement: hasBasement, basement_depth_m: 3.0,
          has_front_shops: (parseInt(numFrontShops, 10) || 0) > 0, num_front_shops: parseInt(numFrontShops, 10) || 0,
          neighbor_side: neighborStr, floors: briefParams.floors,
          setback_front: parseFloat(setFront) || 3, setback_side: parseFloat(setSide) || 2, setback_rear: parseFloat(setRear) || 3,
          structural_system: system, code_profile: code, lang,
          project_name: isAr ? `فيلا ${plotW}×${plotH}` : `Villa ${plotW}×${plotH}`,
        });
      } else if (kind === "apartment") {
        res = await api.generateApartment({
          width: briefParams.plot_w, height: briefParams.plot_h,
          bedrooms: briefParams.bedrooms, bathrooms: briefParams.bathrooms,
          regular_bedrooms: briefParams.regular_bedrooms, kids_rooms: briefParams.kids_rooms,
          num_kitchens: briefParams.num_kitchens, has_master_ensuite: masterEnsuite,
          floors: briefParams.floors, structural_system: system, code_profile: code, lang,
          project_name: isAr ? `شقة ${plotW}×${plotH}` : `Apartment ${plotW}×${plotH}`,
        });
      } else {
        res = await api.editorGenerate(kind, {
          width: briefParams.plot_w, height: briefParams.plot_h,
          plot_w: briefParams.plot_w, plot_h: briefParams.plot_h,
          land_w: briefParams.plot_w, land_h: briefParams.plot_h,
          floors: briefParams.floors, structural_system: system, code_profile: code,
          neighbor_side: neighborStr, lang,
          ...(isMultiUnit ? { num_apartments: briefParams.num_apartments, bedrooms_per_unit: briefParams.bedrooms_per_unit } : {}),
          setback_front: parseFloat(setFront) || 3, setback_side: parseFloat(setSide) || 2, setback_rear: parseFloat(setRear) || 3,
        });
      }
      const title = isAr ? `${selectedKind?.ar} ${plotW}×${plotH}` : `${selectedKind?.en} ${plotW}×${plotH}`;
      setWm({
        model: res.model, kind, floors: briefParams.floors, slab_system: slab,
        has_basement: hasBasement, basement_depth_m: 3.0,
        design_code: (CODES.find((c) => c.id === code)?.dc) || "SBC_304",
        generation_params: { plot_w: briefParams.plot_w, plot_h: briefParams.plot_h }, title,
      });
      toast.success(isAr ? "تم توليد المخطط بنجاح" : "Plan generated");
      navigate("/editor");
    } catch (e) {
      toast.error(String(e.message || e));
    } finally {
      setBusy(null);
    }
  }

  const canNext = step < 4;
  const next = () => setStep((s) => Math.min(4, s + 1));
  const back = () => setStep((s) => Math.max(0, s - 1));

  return (
    <div className="mx-auto max-w-4xl px-4 py-8">
      {/* Header + progress rail */}
      <div className="mb-2 flex items-center gap-2 text-xs font-bold text-emerald-400">
        <Sparkles size={14} /> {isAr ? "معالج التصميم الاحترافي" : "Pro Design Wizard"}
      </div>
      <h1 className="text-3xl font-black sm:text-4xl">
        {isAr ? "ابنِ مشروعك خطوة بخطوة" : "Build your project step by step"}
      </h1>
      <p className="mt-1.5 text-sm text-muted-foreground">
        {isAr ? "معالج مستقل يقودك من الفكرة إلى مخطط هندسي كامل بالقياسات." : "An independent guided flow from idea to a fully-dimensioned plan."}
      </p>

      {/* Stepper */}
      <div className="mt-6 flex items-center gap-1.5 overflow-x-auto pb-1">
        {STEP_META.map((sm, i) => {
          const Icon = sm.icon;
          const active = i === step;
          const done = i < step;
          return (
            <button
              key={i}
              type="button"
              onClick={() => i <= step && setStep(i)}
              data-testid={`dw-step-${i}`}
              className={`flex min-w-fit items-center gap-2 rounded-full border px-3 py-2 text-xs font-bold transition-all duration-300 ${
                active ? "border-primary bg-primary/15 text-primary"
                : done ? "border-emerald-500/40 bg-emerald-500/5 text-emerald-400"
                : "border-border text-muted-foreground"
              }`}
            >
              {done ? <CheckCircle2 size={14} /> : <Icon size={14} />}
              <span className="whitespace-nowrap">{i + 1}. {isAr ? sm.ar : sm.en}</span>
            </button>
          );
        })}
      </div>
      <div className="mt-3 mb-6 flex gap-1.5">
        {STEP_META.map((_, i) => (
          <div key={i} className={`h-1.5 flex-1 rounded-full transition-all duration-500 ${i <= step ? "bg-primary" : "bg-white/10"}`} />
        ))}
      </div>

      <Card className="min-h-[340px]">
        {/* STEP 1 - What to build */}
        {step === 0 && (
          <div className="space-y-4">
            <h2 className="text-xl font-black">{isAr ? "اختر نوع المبنى" : "Choose the building type"}</h2>
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              {KINDS.map((k) => {
                const Icon = k.icon;
                const sel = kind === k.id;
                return (
                  <button
                    key={k.id}
                    type="button"
                    data-testid={`dw-kind-${k.id}`}
                    onClick={() => setKind(k.id)}
                    className={`group relative flex items-start gap-3 overflow-hidden rounded-2xl border p-4 text-start transition-all duration-300 ${
                      sel ? "border-primary bg-primary/10 shadow-[0_0_0_1px_rgba(16,185,129,0.4)]" : "border-border bg-card/50 hover:border-primary/50 hover:bg-card"
                    }`}
                  >
                    <div className={`flex h-11 w-11 shrink-0 items-center justify-center rounded-xl transition-colors ${sel ? "bg-primary text-primary-foreground" : "bg-white/5 text-primary"}`}>
                      <Icon size={22} />
                    </div>
                    <div className="flex-1">
                      <div className="font-black">{isAr ? k.ar : k.en}</div>
                      <div className="mt-0.5 text-xs text-muted-foreground">{isAr ? k.dar : k.den}</div>
                    </div>
                    {sel && <CheckCircle2 className="text-primary" size={18} />}
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {/* STEP 2 - Structure & floors */}
        {step === 1 && (
          <div className="space-y-6">
            <h2 className="text-xl font-black">{isAr ? "الهيكل والطوابق" : "Structure & floors"}</h2>
            <div>
              <p className="mb-2 text-sm font-bold">{isAr ? "النظام الإنشائي" : "Structural system"}</p>
              <div className="grid grid-cols-1 gap-2 sm:grid-cols-3">
                {SYSTEMS.map((s) => (
                  <button key={s.id} type="button" data-testid={`dw-sys-${s.id}`} onClick={() => setSystem(s.id)}
                    className={`rounded-xl border p-3 text-start text-sm font-bold transition-all duration-300 ${system === s.id ? "border-primary bg-primary/10 text-primary" : "border-border bg-card/50 hover:border-primary/40"}`}>
                    {isAr ? s.ar : s.en}
                  </button>
                ))}
              </div>
            </div>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <div>
                <p className="mb-2 text-sm font-bold">{isAr ? "عدد الطوابق" : "Floors"}</p>
                <div className="flex items-center gap-3">
                  <Button variant="outline" size="sm" data-testid="dw-floors-dec" onClick={() => setFloors((f) => Math.max(1, f - 1))}>−</Button>
                  <span className="min-w-[2.5rem] text-center text-2xl font-black text-primary" data-testid="dw-floors-val">{floors}</span>
                  <Button variant="outline" size="sm" data-testid="dw-floors-inc" onClick={() => setFloors((f) => Math.min(10, f + 1))}>+</Button>
                </div>
              </div>
              <div>
                <p className="mb-2 text-sm font-bold">{isAr ? "الكود الإنشائي" : "Design code"}</p>
                <div className="flex flex-wrap gap-2">
                  {CODES.map((c) => (
                    <button key={c.id} type="button" data-testid={`dw-code-${c.id}`} onClick={() => setCode(c.id)}
                      className={`rounded-full border px-3 py-1.5 text-xs font-bold transition-all duration-300 ${code === c.id ? "border-primary bg-primary text-primary-foreground" : "border-border"}`}>
                      {isAr ? c.ar : c.en}
                    </button>
                  ))}
                </div>
              </div>
              <div>
                <p className="mb-1 text-sm font-bold">{isAr ? "طريقة بناء السقف / البلاطة" : "Roof / slab construction"}</p>
                <p className="mb-2 text-xs text-muted-foreground">{isAr ? "تحدد سماكة السقف وكميات الخرسانة والحديد في حصر الكميات." : "Drives the slab thickness and the concrete/steel quantities in the BoQ."}</p>
                <div className="flex flex-wrap gap-2">
                  {ROOFS.map((r) => (
                    <button key={r.id} type="button" data-testid={`dw-roof-${r.id}`} onClick={() => setSlab(r.id)}
                      className={`rounded-full border px-3 py-1.5 text-xs font-bold transition-all duration-300 ${slab === r.id ? "border-primary bg-primary text-primary-foreground" : "border-border"}`}>
                      {isAr ? r.ar : r.en}
                    </button>
                  ))}
                </div>
              </div>
            </div>
            <div className="rounded-xl border border-border bg-card/40 p-3">
              <label className="flex cursor-pointer items-center justify-between">
                <span className="text-sm font-bold">{isAr ? "إضافة قبو" : "Add a basement"}</span>
                <button type="button" role="switch" aria-checked={hasBasement} data-testid="dw-basement-toggle"
                  onClick={() => setHasBasement((v) => !v)}
                  className={`relative h-6 w-11 rounded-full transition-colors ${hasBasement ? "bg-primary" : "bg-white/15"}`}>
                  <span className={`absolute top-0.5 h-5 w-5 rounded-full bg-white transition-all ${hasBasement ? "left-5" : "left-0.5"}`} />
                </button>
              </label>
              {hasBasement && (
                <div className="mt-3 flex flex-wrap gap-2">
                  {BASEMENT_USES.map((u) => (
                    <button key={u.id} type="button" data-testid={`dw-basement-${u.id}`} onClick={() => setBasementUse(u.id)}
                      className={`rounded-full border px-3 py-1.5 text-xs font-bold transition-all duration-300 ${basementUse === u.id ? "border-primary bg-primary/10 text-primary" : "border-border"}`}>
                      {isAr ? u.ar : u.en}
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}

        {/* STEP 3 - Land & neighbours */}
        {step === 2 && (
          <div className="space-y-6">
            <h2 className="text-xl font-black">{isAr ? "الأرض والجيران" : "Land & neighbours"}</h2>
            <div className="grid grid-cols-2 gap-3">
              <Input label={isAr ? "الواجهة / العرض (م)" : "Frontage / Width (m)"} value={plotW} onChange={(e) => setPlotW(e.target.value)} data-testid="dw-plot-w" />
              <Input label={isAr ? "العمق (م)" : "Depth (m)"} value={plotH} onChange={(e) => setPlotH(e.target.value)} data-testid="dw-plot-h" />
            </div>
            <div className="flex items-center gap-2 rounded-xl border border-emerald-500/25 bg-emerald-500/5 p-3 text-sm font-bold text-emerald-400">
              <Ruler size={15} /> {isAr ? `مساحة الأرض: ${area.toLocaleString()} م²` : `Plot area: ${area.toLocaleString()} m²`}
            </div>
            <div>
              <p className="mb-1 text-sm font-bold">{isAr ? "المسافة عن حدود الأرض (ارتداد) - م" : "Distance from plot boundary (setback) - m"}</p>
              <p className="mb-2 text-xs text-muted-foreground">{isAr ? "المسافة النظامية الفارغة بين حد الأرض وجدار المبنى (تفرضها البلدية)." : "The regulated empty distance between the plot boundary and the building wall."}</p>
              <div className="grid grid-cols-3 gap-3">
                <Input label={isAr ? "أمامي (الشارع)" : "Front (street)"} value={setFront} onChange={(e) => setSetFront(e.target.value)} data-testid="dw-setback-front" />
                <Input label={isAr ? "جانبي" : "Side"} value={setSide} onChange={(e) => setSetSide(e.target.value)} data-testid="dw-setback-side" />
                <Input label={isAr ? "خلفي" : "Rear"} value={setRear} onChange={(e) => setSetRear(e.target.value)} data-testid="dw-setback-rear" />
              </div>
            </div>
            <div>
              <p className="mb-1 text-sm font-bold">{isAr ? "أين يوجد جيران ملاصقون؟" : "Where are attached neighbours?"}</p>
              <p className="mb-2 text-xs text-muted-foreground">{isAr ? "اختر كل الجهات التي يوجد بها جار ملاصق (جدار مشترك على الحد). الشارع دائمًا في الأمام." : "Pick every side with an attached neighbour (party wall on the boundary). The street is always at the front."}</p>
              <div className="flex flex-wrap gap-2">
                {NEIGHBOR_SIDES.map((n) => (
                  <button key={n.id} type="button" data-testid={`dw-neighbor-${n.id}`} onClick={() => toggleNeighbor(n.id)}
                    className={`rounded-full border px-3 py-1.5 text-xs font-bold transition-all duration-300 ${neighbors.includes(n.id) ? "border-primary bg-primary/10 text-primary" : "border-border"}`}>
                    {isAr ? n.ar : n.en}
                  </button>
                ))}
                {neighbors.length === 0 && (
                  <span className="rounded-full border border-emerald-500/30 bg-emerald-500/10 px-3 py-1.5 text-xs font-bold text-emerald-400">{isAr ? "أرض مفردة (بلا جيران)" : "Detached (no neighbours)"}</span>
                )}
              </div>
            </div>
          </div>
        )}

        {/* STEP 4 - Spaces & amenities */}
        {step === 3 && (
          <div className="space-y-6">
            <h2 className="text-xl font-black">{isAr ? "الفراغات والمرافق" : "Spaces & amenities"}</h2>
            {isResidential ? (
              <>
                <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
                  <Input type="number" label={isAr ? "غرف نوم عادية" : "Regular bedrooms"} value={regularBeds} onChange={(e) => setRegularBeds(e.target.value)} data-testid="dw-regular-beds" />
                  <Input type="number" label={isAr ? "غرف أطفال (أصغر)" : "Kids rooms (smaller)"} value={kidsRooms} onChange={(e) => setKidsRooms(e.target.value)} data-testid="dw-kids-rooms" />
                  <Input type="number" label={isAr ? "حمامات" : "Bathrooms"} value={baths} onChange={(e) => setBaths(e.target.value)} data-testid="dw-baths" />
                  <Input type="number" label={isAr ? "مطابخ" : "Kitchens"} value={kitchens} onChange={(e) => setKitchens(e.target.value)} data-testid="dw-kitchens" />
                  {kind === "villa" && (
                    <Input type="number" label={isAr ? "محلات واجهة" : "Front shops"} value={numFrontShops} onChange={(e) => setNumFrontShops(e.target.value)} data-testid="dw-front-shops" />
                  )}
                </div>
                <label className="flex cursor-pointer items-center justify-between rounded-xl border border-border bg-card/40 p-3">
                  <span className="text-sm font-bold">{isAr ? "غرفة ماستر بحمام خاص (أكبر)" : "Master bedroom with ensuite (larger)"}</span>
                  <button type="button" role="switch" aria-checked={masterEnsuite} data-testid="dw-master-toggle"
                    onClick={() => setMasterEnsuite((v) => !v)}
                    className={`relative h-6 w-11 rounded-full transition-colors ${masterEnsuite ? "bg-primary" : "bg-white/15"}`}>
                    <span className={`absolute top-0.5 h-5 w-5 rounded-full bg-white transition-all ${masterEnsuite ? "left-5" : "left-0.5"}`} />
                  </button>
                </label>
                <div className="rounded-xl border border-primary/20 bg-primary/5 p-3 text-xs font-bold text-primary">
                  {isAr ? `إجمالي غرف النوم: ${totalBeds} (ماستر ${masterEnsuite ? 1 : 0} + عادية ${parseInt(regularBeds, 10) || 0} + أطفال ${parseInt(kidsRooms, 10) || 0})`
                        : `Total bedrooms: ${totalBeds}`}
                </div>
                <div>
                  <p className="mb-2 text-sm font-bold">{isAr ? "المرافق" : "Amenities"}</p>
                  <div className="flex flex-wrap gap-2">
                    {AMENITIES.map((a) => (
                      <button key={a.id} type="button" data-testid={`dw-amen-${a.id}`} onClick={() => toggleAmen(a.id)}
                        className={`rounded-full border px-4 py-2 text-sm font-bold transition-all duration-300 ${has(a.id) ? "border-primary bg-primary text-primary-foreground" : "border-border bg-card/50 hover:border-primary/40"}`}>
                        {isAr ? a.ar : a.en}
                      </button>
                    ))}
                  </div>
                </div>
              </>
            ) : isMultiUnit ? (
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-3">
                  <Input type="number" label={isAr ? "عدد الشقق بالطابق" : "Apartments per floor"} value={numApartments} onChange={(e) => setNumApartments(e.target.value)} data-testid="dw-num-apartments" />
                  <Input type="number" label={isAr ? "غرف نوم لكل شقة (0 = تلقائي)" : "Bedrooms per unit (0 = auto)"} value={bedroomsPerUnit} onChange={(e) => setBedroomsPerUnit(e.target.value)} data-testid="dw-bedrooms-per-unit" />
                </div>
                <div className="rounded-xl border border-primary/20 bg-primary/5 p-3 text-xs font-bold text-primary">
                  {isAr
                    ? "على الأرض الضيقة العميقة تُرتَّب الشقق أمامية/خلفية على امتداد العمق مع درج ومدخل من الشارع؛ وعلى الأرض العريضة تُوزَّع على جانبي ممر أوسط. سيُظهر لك المخطط العدد الذي يتّسع فعليًا للأرض."
                    : "On a narrow-deep plot the units stack front/back with a street entry; on a wide plot they sit either side of a central corridor. The plan shows how many actually fit."}
                </div>
              </div>
            ) : (
              <div className="rounded-xl border border-border bg-card/40 p-4 text-sm text-muted-foreground">
                {isAr
                  ? `سيولّد ${selectedKind?.ar} توزيعات متنوعة تلقائياً حسب مساحة الأرض والطوابق. اضغط "التالي" لمراجعة الملخص والتوليد.`
                  : `${selectedKind?.en} layouts are auto-composed from the plot size & floors. Continue to review and generate.`}
              </div>
            )}
          </div>
        )}

        {/* STEP 5 - Review & generate */}
        {step === 4 && (
          <div className="space-y-5">
            <h2 className="text-xl font-black">{isAr ? "مراجعة المشروع" : "Review your project"}</h2>
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <ReviewRow icon={selectedKind?.icon || Home} label={isAr ? "نوع المبنى" : "Type"} value={isAr ? selectedKind?.ar : selectedKind?.en} />
              <ReviewRow icon={Layers} label={isAr ? "الهيكل / السقف / الكود" : "Structure / Roof / Code"} value={`${isAr ? SYSTEMS.find((s) => s.id === system)?.ar : SYSTEMS.find((s) => s.id === system)?.en} · ${isAr ? ROOFS.find((r) => r.id === slab)?.ar : ROOFS.find((r) => r.id === slab)?.en} · ${isAr ? CODES.find((c) => c.id === code)?.ar : CODES.find((c) => c.id === code)?.en} · ${floors} ${isAr ? "طابق" : "fl"}${hasBasement ? (isAr ? " + قبو" : " + basement") : ""}`} />
              <ReviewRow icon={Ruler} label={isAr ? "الأرض" : "Land"} value={`${plotW}×${plotH} م · ${area.toLocaleString()} م²`} />
              <ReviewRow icon={Compass} label={isAr ? "الجيران / الارتداد" : "Neighbours / Setback"} value={`${neighbors.length ? neighbors.map((id) => { const n = NEIGHBOR_SIDES.find((x) => x.id === id); return isAr ? n?.ar : n?.en; }).join("، ") : (isAr ? "بلا جيران" : "None")} · ${setFront}/${setSide}/${setRear}م`} />
              {isResidential && (
                <ReviewRow icon={DoorOpen} label={isAr ? "الفراغات" : "Spaces"} value={isAr ? `${totalBeds} غرف · ${baths} حمام · ${kitchens} مطبخ` : `${totalBeds} beds · ${baths} bath · ${kitchens} kitchen`} />
              )}
              {isResidential && amen.length > 0 && (
                <ReviewRow icon={Sparkles} label={isAr ? "المرافق" : "Amenities"} value={amen.map((id) => (isAr ? AMENITIES.find((a) => a.id === id)?.ar : AMENITIES.find((a) => a.id === id)?.en)).join("، ")} />
              )}
            </div>

            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <Button size="lg" onClick={goVariants} data-testid="dw-generate-variants">
                <Images size={18} /> {isAr ? "ولّد 10 مخططات متنوعة" : "Generate 10 variants"}
              </Button>
              <Button size="lg" variant="accent" onClick={generateOne} loading={busy === "one"} data-testid="dw-generate-one">
                <Zap size={18} /> {isAr ? "ولّد مخططاً واحداً فوراً" : "Generate one plan now"}
              </Button>
            </div>
            <p className="flex items-center gap-1.5 text-xs text-muted-foreground">
              <CheckCircle2 size={13} className="text-emerald-400" />
              {isAr ? "قواعد وخوارزميات هندسية مع فحوصات آلية - ليست بديلاً عن مهندس مرخّص." : "Engineering rules with automatic checks - not a substitute for a licensed engineer."}
            </p>
          </div>
        )}
        {busy === "one" && <div className="mt-4 flex justify-center"><Spinner className="text-primary" /></div>}
      </Card>

      {/* Nav */}
      <div className="mt-5 flex justify-between gap-3">
        <Button variant="outline" disabled={step === 0} onClick={back} data-testid="dw-back">
          <ArrowLeft size={16} /> {isAr ? "السابق" : "Back"}
        </Button>
        {canNext && (
          <Button onClick={next} data-testid="dw-next">
            {isAr ? "التالي" : "Next"} <ArrowRight size={16} />
          </Button>
        )}
      </div>
    </div>
  );
}

function ReviewRow({ icon: Icon, label, value }) {
  return (
    <div className="flex items-start gap-3 rounded-xl border border-border bg-card/40 p-3">
      <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary">
        <Icon size={17} />
      </div>
      <div className="min-w-0">
        <div className="text-xs text-muted-foreground">{label}</div>
        <div className="truncate text-sm font-bold">{value}</div>
      </div>
    </div>
  );
}
